import { useMutation, useQuery } from '@tanstack/react-query';
import { FileDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ErroApi } from '../api/cliente';
import { tabelas as apiTabelas } from '../api/endpoints';
import { Aviso } from '../components/Aviso';
import { Botao } from '../components/Botao';
import { Campo } from '../components/Campo';
import { Pagina } from '../components/Pagina';
import { SeletorTabela } from '../components/SeletorTabela';
import { lerNumero, paraCampo } from '../lib/formatos';

/** PDF para os representantes, sempre com os preços atuais do CIGAM (SPEC §8.7). */
export function PdfTabela() {
  const [params, setParams] = useSearchParams();
  const tabela = params.get('tabela') ?? '';
  const [pedidoMinimo, setPedidoMinimo] = useState('');
  const [valorMinParcela, setValorMinParcela] = useState('');
  const [mensagem, setMensagem] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null);

  const condicoes = useQuery({ queryKey: ['tabelas', tabela, 'condicoes'], queryFn: () => apiTabelas.condicoes(tabela), enabled: Boolean(tabela) });
  useEffect(() => {
    if (!condicoes.data) return;
    setPedidoMinimo(paraCampo(condicoes.data.condicoes.pedidoMinimo, 2));
    setValorMinParcela(paraCampo(condicoes.data.condicoes.valorMinParcela, 2));
  }, [condicoes.data]);

  const gerar = useMutation({
    mutationFn: async () => {
      const pm = lerNumero(pedidoMinimo);
      const vp = lerNumero(valorMinParcela);
      if ((pm !== null && (Number.isNaN(pm) || pm < 0)) || (vp !== null && (Number.isNaN(vp) || vp < 0))) {
        throw new ErroApi(400, 'VALOR_INVALIDO', 'Confira o pedido mínimo e o valor mínimo da parcela.');
      }
      const atuais = condicoes.data?.condicoes;
      if (pm !== (atuais?.pedidoMinimo ?? null) || vp !== (atuais?.valorMinParcela ?? null)) {
        await apiTabelas.salvarCondicoes(tabela, { pedidoMinimo: pm, valorMinParcela: vp });
        await condicoes.refetch();
      }
      await apiTabelas.baixarPdf(tabela);
    },
    onSuccess: () => setMensagem({ tipo: 'ok', texto: 'PDF gerado com os preços atuais do CIGAM.' }),
    onError: (e) => setMensagem({ tipo: 'erro', texto: e instanceof ErroApi ? e.message : 'Não foi possível gerar o PDF.' }),
  });

  return (
    <Pagina titulo="PDF da tabela" contexto="Tabela para os representantes, com os preços atuais do CIGAM. Não mostra custo, margem nem parâmetros.">
      <div className="flex max-w-xl flex-col gap-4">
        <SeletorTabela
          valor={tabela}
          aoMudar={(codigo) => {
            setParams(codigo ? { tabela: codigo } : {}, { replace: true });
            setMensagem(null);
          }}
        />
        {tabela && (
          <>
            <div className="grid grid-cols-2 gap-3 rounded-painel border border-linha bg-superficie p-4">
              <Campo rotulo="Pedido mínimo (R$)" inputMode="decimal" value={pedidoMinimo} onChange={(e) => setPedidoMinimo(e.target.value)} ajuda="Vazio = não aparece." disabled={condicoes.isPending} />
              <Campo rotulo="Valor mínimo da parcela (R$)" inputMode="decimal" value={valorMinParcela} onChange={(e) => setValorMinParcela(e.target.value)} ajuda="Vazio = não aparece." disabled={condicoes.isPending} />
            </div>
            <p className="text-[13px] text-tinta-2">
              O rodapé traz a validade da tabela no CIGAM e as mensagens padrão definidas pelo administrador. O nome do arquivo segue o padrão{' '}
              <code className="font-mono">Tabela_{tabela}_AAAA-MM-DD.pdf</code>.
            </p>
            {mensagem && <Aviso tipo={mensagem.tipo}>{mensagem.texto}</Aviso>}
            <div>
              <Botao variante="principal" carregando={gerar.isPending} onClick={() => gerar.mutate()}>
                <FileDown aria-hidden className="size-4" /> Gerar PDF
              </Botao>
            </div>
          </>
        )}
      </div>
    </Pagina>
  );
}
